import "leaflet/dist/leaflet.css";
import { useIsSmallViewport } from "hooks/useIsSmallViewport";
import { useShownColorScheme } from "hooks/useShownColorScheme";
import { lazy, memo, useEffect, useRef, Suspense } from "react";

import {
  RasterTileLayer,
  RasterMapID,
} from "components/LeafletMap/CachedRasterTileLayer";
import { ChartMarker } from "components/LeafletMap/ChartMarker";
import { ContextLifter } from "components/LeafletMap/ContextLifter";
import {
  SearchGeocoderControl,
  MouseCoordinateControl,
  CustomZoomControl,
} from "components/LeafletMap/MapControls";
import { GeolocationControl } from "components/LeafletMap/MapControls/GeolocationControl";
import { MergedMapPublicLegendHelpControl } from "components/LeafletMap/MapControls/MergedMapPublicLegendHelpControl/MergedMapPublicLegendHelpControl";
import { ScaleControlAutoUnit } from "components/LeafletMap/MapControls/ScaleControlAutoUnit/ScaleControlAutoUnit";
import {
  isTangramLayer,
  TangramMapID,
} from "components/LeafletMap/TangramLayersMeta";
import { UrlParamsHandler } from "components/LeafletMap/UrlParamsHandler";
import { WaypointAddingIndicatorWrapper } from "components/LeafletMap/WaypointAddingIndicator/WaypointAddingIndicator";
import { MAP_SETTINGS } from "config/constants";
import { useMapContext } from "stores/useMapContext";
import { useMapSettingsSaver } from "hooks/useMapSettingsSaver";
import { useOpenSharedTrack } from "hooks/useOpenSharedTrack";
import { MapContainer, Marker, Pane, useMapEvents } from "react-leaflet";
import { RenderInTransition } from "components/RenderInTransition";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { useShowCesiumMap } from "stores/genericStore/GenericContext";
import { useOptions } from "stores/optionsStore/OptionsContext";
import {
  getQueryLatLng,
  getQueryLocationID,
  getQueryZoom,
} from "utils/getQueryParam";
import { getShownMapId } from "utils/map/getShownMapId";
import { MeasurementWrapper } from "./MeasurementContainer";
import { RoutingContainer } from "./RoutingContainer";
import { UserTracksContainer } from "./UserTracksContainer";
import { UserWaypointsContainer } from "./UserWaypointsContainer";
import { LoadingOverlay } from "@mantine/core";
import { ControlActionIcon } from "components/LeafletMap/MapControls/ControlActionIcon";
import { MapControlPaper } from "components/LeafletMap/MapControls/MapControlPaper";
import { LeafletControlWrapper } from "components/LeafletMap/Utils/LeafletControlWrapper";
import { defaultIcon } from "components/LeafletMap/Icons";
import { GPXViewerDownloadDialog } from "containers/UI/GPXViewerDownloadDialog";
import { useMobileMenu } from "stores/useMobileMenu";
import { useTranslation } from "react-i18next";
import "./MapViewContainer.css";

// The map renderers are big (Tangram, Cesium), so they're split into separate chunks
// to keep them from delaying the first render of the app
const TangramLayer = lazy(() =>
  import("components/LeafletMap/TangramTileLayer").then((module) => ({
    default: module.TangramLayer,
  }))
);
const CesiumMap = lazy(() =>
  import("./CesiumMap").then((module) => ({ default: module.CesiumMap }))
);

function BurgerIcon() {
  const isSmallViewport = useIsSmallViewport();
  const isRunningInsideElectron =
    /electron/i.test(navigator.userAgent) || globalThis.isElectron;
  const { isMobileMenuOpen, toggleMobileMenu } = useMobileMenu();
  const { t } = useTranslation();

  return (
    <LeafletControlWrapper position={"topright"}>
      {isSmallViewport && !isRunningInsideElectron ? (
        <MapControlPaper>
          <ControlActionIcon
            onClick={() => toggleMobileMenu()}
            aria-label={t("mobile_menu.open_label")}
            aria-expanded={isMobileMenuOpen}
          >
            <FontAwesomeIcon icon={faBars} />
          </ControlActionIcon>
        </MapControlPaper>
      ) : null}
    </LeafletControlWrapper>
  );
}

const GlobalMapEventsInjector = () => {
  useMapSettingsSaver();
  useMapEvents({ contextmenu: () => false });
  return null;
};

type MapLayersAndControlsProps = {
  mapId: ReturnType<typeof getShownMapId>;
  isSmallViewport: boolean;
  /** The location of a shared location link, while it's shown */
  sharedLat?: number;
  sharedLng?: number;
};

/**
 * The map's layers and controls. Memoized: MapViewContainer rerenders when any option changes (and with the map),
 * the layers and controls only when these props or the state they use themselves change.
 */
const MapLayersAndControls = memo(function MapLayersAndControls({
  mapId: selectedMapId,
  isSmallViewport,
  sharedLat,
  sharedLng,
}: MapLayersAndControlsProps) {
  return (
    <RenderInTransition>
      {isTangramLayer(selectedMapId) ? (
        <Suspense fallback={null}>
          <TangramLayer mapId={selectedMapId as TangramMapID} />
        </Suspense>
      ) : (
        <RasterTileLayer mapId={selectedMapId as RasterMapID} />
      )}
      <Pane name="hotlinePane" style={{ zIndex: 410, pointerEvents: "none" }} />
      <ContextLifter />
      <GlobalMapEventsInjector />
      <UserWaypointsContainer />
      <UserTracksContainer />
      <UrlParamsHandler />
      <RoutingContainer />
      <MeasurementWrapper />
      {!isSmallViewport ? (
        <MouseCoordinateControl position="bottomright" />
      ) : null}
      <WaypointAddingIndicatorWrapper />
      <BurgerIcon />
      <MergedMapPublicLegendHelpControl position="topright" />
      <SearchGeocoderControl position="topright" />
      <GeolocationControl position="topright" />
      <CustomZoomControl position="topright" mapId={selectedMapId} />
      <ScaleControlAutoUnit position="bottomleft" />
      <ChartMarker />
      {sharedLat !== undefined && sharedLng !== undefined ? (
        <>
          <Marker position={[sharedLat, sharedLng]} icon={defaultIcon} />
          <GPXViewerDownloadDialog />
        </>
      ) : null}
    </RenderInTransition>
  );
});

/** Memoized (below): it's rendered by MapWrapper, which rerenders with the map and the map's size */
const MapViewContainerComponent = () => {
  const {
    state: { allowPremiumFeatures, mapType: mapLayerID },
    loading,
  } = useOptions();
  const { map } = useMapContext();
  const showCesiumMap = useShowCesiumMap();
  const colorScheme = useShownColorScheme();
  const isSmallViewport = useIsSmallViewport();
  const dynamicCSSLoaded = useRef(false);
  const isSharedLocation = useRef(getQueryLocationID());

  useEffect(() => {
    if (dynamicCSSLoaded.current === false) {
      const isRunningInsideElectron =
        /electron/i.test(navigator.userAgent) || globalThis.isElectron;

      if (!isRunningInsideElectron) {
        import("./NavbarOffset.css")
          .then(() => {
            dynamicCSSLoaded.current = true;
          })
          .catch((error: unknown) =>
            console.error(
              "Could not dynamically import navbar offsset CSS: ",
              error instanceof Error ? error.message : error
            )
          );
      }
    }
  }, []);

  const selectedMapId = getShownMapId(mapLayerID, allowPremiumFeatures);
  useOpenSharedTrack(map);

  useEffect(() => {
    if (map && isSharedLocation.current) {
      const listener = () => {
        isSharedLocation.current = null;
        map.off("click", listener);
      };

      map.on("click", listener);
    }
  }, [map]);

  const latLng = getQueryLatLng();
  const showSharedLocation =
    Boolean(isSharedLocation.current) && latLng !== null;
  const zoom = isSharedLocation.current ? 16 : getQueryZoom();
  const mapCenterProp = latLng ? { center: latLng } : null;
  const zoomProp = zoom ? { zoom } : null;

  return (
    <>
      <LoadingOverlay
        zIndex={1000}
        loaderProps={{
          size: "lg",
          type: "bars",
          color: colorScheme === "dark" ? "#8566e0" : "#6741d9",
        }}
        visible={loading}
        overlayProps={{
          blur: 2,
          color: colorScheme === "dark" ? "#161719" : "#fff",
          opacity: 0.8,
        }}
      />
      {showCesiumMap ? (
        <Suspense fallback={null}>
          <CesiumMap />
        </Suspense>
      ) : (
        // Created after the app's UI is committed, see RenderInTransition (the placeholder takes the map's space)
        <RenderInTransition fallback={<div className="leaflet-container" />}>
          <MapContainer
            {...MAP_SETTINGS}
            {...mapCenterProp}
            {...zoomProp}
            maxZoom={20}
          >
            <MapLayersAndControls
              mapId={selectedMapId}
              isSmallViewport={isSmallViewport}
              sharedLat={showSharedLocation ? latLng[0] : undefined}
              sharedLng={showSharedLocation ? latLng[1] : undefined}
            />
          </MapContainer>
        </RenderInTransition>
      )}
    </>
  );
};

export const MapViewContainer = memo(MapViewContainerComponent);
